import { useState } from "react";
import SectionCard from "./SectionCard";

type Props = {
  onChange: (msg: string) => void;
};

type Channel = "email" | "sms" | "push";

type Pref = {
  id: string;
  label: string;
  hint: string;
  channels: Record<Channel, boolean>;
};

const initial: Pref[] = [
  {
    id: "new_booking",
    label: "New booking",
    hint: "A customer just booked a job",
    channels: { email: true, sms: true, push: true },
  },
  {
    id: "cancellations",
    label: "Cancellations",
    hint: "Job cancelled within 24 hrs",
    channels: { email: true, sms: true, push: false },
  },
  {
    id: "review",
    label: "New review",
    hint: "Customer left a rating",
    channels: { email: true, sms: false, push: true },
  },
  {
    id: "payout_failed",
    label: "Payout failed",
    hint: "ACH or instant transfer didn't go through",
    channels: { email: true, sms: true, push: true },
  },
  {
    id: "digest",
    label: "Weekly digest",
    hint: "Monday morning recap of last week",
    channels: { email: true, sms: false, push: false },
  },
];

const channelLabel: Record<Channel, string> = {
  email: "Email",
  sms: "SMS",
  push: "Push",
};

const channelIcon: Record<Channel, string> = {
  email: "ri-mail-line",
  sms: "ri-chat-1-line",
  push: "ri-smartphone-line",
};

export default function NotificationsSection({ onChange }: Props) {
  const [prefs, setPrefs] = useState<Pref[]>(initial);

  const toggle = (id: string, ch: Channel) => {
    setPrefs((arr) =>
      arr.map((p) =>
        p.id === id
          ? { ...p, channels: { ...p.channels, [ch]: !p.channels[ch] } }
          : p,
      ),
    );
    const pref = prefs.find((p) => p.id === id);
    if (pref) {
      const turningOn = !pref.channels[ch];
      onChange(
        `${pref.label} · ${channelLabel[ch]} ${turningOn ? "on" : "off"}`,
      );
    }
  };

  return (
    <SectionCard
      title="Notifications"
      description="Pick how you want to be reached when things happen."
    >
      <div className="hidden sm:grid grid-cols-[1fr_repeat(3,72px)] gap-3 px-1 pb-2 text-[10px] uppercase tracking-widest text-foreground-500">
        <span>Event</span>
        <span className="text-center">Email</span>
        <span className="text-center">SMS</span>
        <span className="text-center">Push</span>
      </div>
      <ul className="divide-y divide-background-200">
        {prefs.map((p) => (
          <li
            key={p.id}
            className="py-3 grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_repeat(3,72px)] gap-3 items-center"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground-950">
                {p.label}
              </p>
              <p className="text-[11px] text-foreground-600 truncate">{p.hint}</p>
            </div>
            <div className="col-span-1 sm:col-span-3 sm:contents flex items-center gap-2 justify-end">
              {(["email", "sms", "push"] as Channel[]).map((ch) => (
                <button
                  key={ch}
                  onClick={() => toggle(p.id, ch)}
                  className={`w-10 h-10 sm:w-12 sm:h-8 flex items-center justify-center rounded-md text-sm cursor-pointer transition-colors ${
                    p.channels[ch]
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-500"
                  }`}
                  aria-label={`${channelLabel[ch]} for ${p.label}`}
                  title={channelLabel[ch]}
                >
                  <i className={`${channelIcon[ch]} text-base`}></i>
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}